// ═══════════════════════════════════════════════════════════════════════════
//  THE OPERATOR SETUP HUB — `#setup/<electionId>`. The Synod Admin's screen for
//  getting a sitting ready to vote.
//
//  ── A GATED HUB, NOT A LINEAR FUNNEL (Miranda's north-star) ────────────────
//  Two tracks run in parallel and converge at Open. The BUILD track prepares the
//  ballots (draft → ready) and, in the follow-on, builds them; the ENLIVEN track
//  freezes the roll, from which the attendance register (its own screen) and then
//  a quorum declaration become possible. Neither waits on the other until Open: a
//  ballot is built before the roll is frozen (SO 15.5), and the worker does not
//  gate it otherwise. So this hub OFFERS each act only when the state permits it
//  and GUIDES the order — Open appears once the roll is frozen — rather than
//  claiming to control a sequence the server does not enforce.
//
//  ── IT AUTHORISES NOTHING ──────────────────────────────────────────────────
//  Reaching this fragment gets you the screen; every act re-resolves an admin
//  grant server-side on the request that runs it (`src/routes/setup.ts`). A person
//  who types somebody else's election id is refused, not admitted. `identity` is
//  handed down for the header's name and nothing else.
//
//  ── NO FIGURE FROM A POLL, ANYWHERE ────────────────────────────────────────
//  The roll's counts are of PEOPLE, not ballots; the quorum figures are the
//  operator's own declaration; the only times shown are each ballot's own published
//  schedule. Nothing on this screen is a tally, a turnout or a countdown — there is
//  none to leak (registrar 2026-08-16).
//
//  **THE REASON IS THE ROUTE'S COLUMN LIST, NOT THE BALLOT'S STATE**, and the
//  difference started to matter the day `GET /api/setup/:electionId` widened to list
//  OPEN ballots so the hub could offer them more time. "A draft has no votes" was the
//  old reason and it is no longer true of every row here — an open poll has votes.
//  What keeps this screen figure-free is that the route selects no poll figure at all,
//  which is a fact a reviewer checks against the SELECT (`src/routes/setup.ts`).
//
//  ── WHAT IS DEFERRED, STATED PLAINLY ───────────────────────────────────────
//  The in-app ballot BUILDER (the position/motion form → `POST …/ballot`) is the
//  next phase (P3a-2). This hub takes existing drafts from draft to open; where the
//  builder attaches is a seam note in the ballots panel, not a stub. The ZOHO roll
//  PULL is P3b: its button reports "not yet available" today. What is NOT deferred is
//  getting electors onto the roll — the roll panel takes a roster the registrar has
//  pasted (a Zoho Contacts export, which needs no credentials this worker does not
//  have) and loads it through the same `POST …/roll` the pull will use. Both paths
//  land on one function below, so the request, the refusal and the announcement
//  cannot come apart between them.
// ═══════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { PortalHeader } from '@cca/ui';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import { ModeBand } from '../results/ModeBand';
import { asBallotMode } from '../paper';
import { portalNameFor } from '../portal-name';
import { BallotBuilder } from './BallotBuilder';
import { BallotsPanel } from './BallotsPanel';
import { ExtendStep } from './ExtendStep';
import { OpenPollsPanel } from './OpenPollsPanel';
import { OpenStep } from './OpenStep';
import { RollPanel } from './RollPanel';
import { wallClockInWords } from './SchedulePanel';
import { REGISTRAR_EMAIL, refusalSentence, type RefusalAct } from './copy';
import {
  api,
  DecidesApiError,
  NO_REFUSAL_DETAILS,
  type CreateBallotSpec,
  type DraftBallot,
  type Identity,
  type QuorumDeclaration,
  type RefusalDetails,
  type RegisterHouseCount,
  type RosterElector,
  type RosterPull,
} from '../api/client';

/** The roll as this hub holds it. Frozen carries the digest and the register's
 *  counts (the Open step's reference); not-frozen carries nothing more — the
 *  worker has no pre-freeze count to report (see {@link RollPanel}). */
type RollInfo =
  { phase: 'frozen'; rollDigest: string; register: RegisterHouseCount[] } | { phase: 'not-frozen' };

interface HubData {
  sitting: { electionId: string; mode: string; name: string };
  drafts: DraftBallot[];
  roll: RollInfo;
}

type Screen =
  { status: 'loading' } | { status: 'refused'; code: string } | { status: 'ready'; data: HubData };

/** Where the operator is WITHIN a ready hub. The two result arms exist so the two
 *  points of no return each land on a heading that takes focus (Sexton's law:
 *  "moves focus to the result heading"), rather than folding back into the hub
 *  silently.
 *
 *  `extending` is a STEP and gets no result arm of its own, and the asymmetry is
 *  deliberate rather than an omission: freezing the roll and opening a ballot cannot
 *  be undone, so each lands on its own screen. Giving a poll more time can be done
 *  again, so it returns to the hub and reports in the announcement region — the same
 *  disposition `doCreate` takes for a draft, which is likewise reversible. */
type View =
  | { kind: 'hub' }
  | { kind: 'building' }
  | { kind: 'opening'; ballot: DraftBallot }
  | { kind: 'extending'; ballot: DraftBallot }
  | { kind: 'roll-frozen'; rollDigest: string }
  | { kind: 'ballot-opened'; ballot: DraftBallot };

interface Props {
  electionId: string;
  /** Read for the header's name only — the route re-resolves the admin grant on
   *  every request it serves. */
  identity: Identity;
  onBack: () => void;
}

/** Detect the roll phase by asking the attendance register: it answers only once
 *  the roll is frozen, and `roll_not_frozen` (409) otherwise. Any OTHER refusal is
 *  unexpected after the draft-list gate passed, and is left to bubble to the
 *  refused screen. */
async function detectRoll(electionId: string): Promise<RollInfo> {
  try {
    const reg = await api.register(electionId);
    return { phase: 'frozen', rollDigest: reg.sitting.rollDigest, register: reg.houses };
  } catch (err) {
    if (err instanceof DecidesApiError && err.code === 'roll_not_frozen') {
      return { phase: 'not-frozen' };
    }
    throw err;
  }
}

export function SetupPage({ electionId, identity, onBack }: Props): JSX.Element {
  const [screen, setScreen] = useState<Screen>({ status: 'loading' });
  const [view, setView] = useState<View>({ kind: 'hub' });
  /** The single announcement region's content (role="status") — a stage word,
   *  never a figure. Set by each act, cleared when the next begins. */
  const [notice, setNotice] = useState<string | null>(null);
  /** The last failed act, shown as an alert: the wire code, which act it refused, AND
   *  the refusal's own body. All three, because one code can mean two different things
   *  depending on what was attempted (`copy.ts` `refusalSentence`) and several carry
   *  the figures their repair needs — and as ONE value rather than three states, so the
   *  code, the act and the figures cannot come apart. */
  const [actError, setActError] = useState<{
    code: string;
    act: RefusalAct | null;
    details: RefusalDetails;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  /** The ballot id of the act in flight, so one row's buttons lock without locking
   *  the others. */
  const [pending, setPending] = useState<string | null>(null);
  /** Electors loaded THIS session (the only pre-freeze count the worker gives). */
  const [loaded, setLoaded] = useState<number | null>(null);
  /** The pull's "not yet available" (P3b) note, kept apart from `actError` because
   *  it is not a failure — the endpoint is simply not built. */
  const [pullNote, setPullNote] = useState<string | null>(null);
  /** Set when a sub-view is about to hand the hub back, so the hub lands focus on its
   *  heading. The button that was pressed has just unmounted and a freshly-mounted
   *  role="status" is unreliable on its own — the same "place focus on the outcome"
   *  intent as {@link FocusHeading}, but the hub heading is a plain element, so this is
   *  a pending flag consumed by the effect below rather than a mount effect.
   *
   *  Set by both reversible acts that return here — creating a draft and extending a
   *  poll — and by the way back out of the builder and the open step (straight back, or
   *  through their leave confirm), which unmounts the pressed control just the same. It
   *  is named for the return rather than for one act, because a flag called `…OnCreate`
   *  being set by the extend path is the kind of drift that makes the next reader
   *  believe the wrong thing about when focus moves. */
  const focusHubHeadingOnReturn = useRef(false);
  const hubHeading = useRef<HTMLHeadingElement>(null);

  /** Fetch the drafts and the roll phase, WITHOUT a loading flash — for the initial
   *  mount (which shows one via {@link load}) this is the second half; for a
   *  post-act refresh it keeps the screen still. */
  const reloadData = useCallback(async (): Promise<void> => {
    const list = await api.listDrafts(electionId);
    const roll = await detectRoll(electionId);
    setScreen({
      status: 'ready',
      data: { sitting: list.sitting, drafts: list.ballots, roll },
    });
  }, [electionId]);

  const load = useCallback(async (): Promise<void> => {
    setScreen({ status: 'loading' });
    try {
      await reloadData();
    } catch (err) {
      setScreen({
        status: 'refused',
        code: err instanceof DecidesApiError ? err.code : 'unreachable',
      });
    }
  }, [reloadData]);

  useEffect(() => {
    void load();
  }, [load]);

  // When a sub-view hands the hub back, move focus off the (now-unmounted) button onto
  // the hub heading, so a keyboard/SR operator is placed on the sitting rather than
  // dropped to <body>. A way back is no different from a completed act here: the
  // control the operator pressed went with the sub-view, so there is no "where they
  // put it" left to keep. Guarded by a flag — set in doCreate, doExtend and the
  // builder's and the open step's onCancel — so it fires on those returns and not on
  // every render of the hub.
  useEffect(() => {
    if (view.kind === 'hub' && focusHubHeadingOnReturn.current) {
      focusHubHeadingOnReturn.current = false;
      hubHeading.current?.focus();
    }
  }, [view]);

  /** Re-fetch the draft list alone (its states moved) and fold it into the ready
   *  data — for the lifecycle acts, which do not touch the roll. */
  const refreshDrafts = useCallback(async (): Promise<void> => {
    const list = await api.listDrafts(electionId);
    setScreen((current) =>
      current.status === 'ready'
        ? { status: 'ready', data: { ...current.data, drafts: list.ballots } }
        : current,
    );
  }, [electionId]);

  /** The shared failure disposition: store the code for display, and re-sync the
   *  drafts when the refusal says this screen's belief about a ballot's state is
   *  stale.
   *
   *  `act` names what was attempted, for the codes that mean different things
   *  depending on it. It is kept only for the ACT'S OWN refusals (409/422): the three
   *  gates run before every setup route and refuse the PERSON, not the act, so a 403
   *  `ambiguous_identity` from `requireAdmin` is about the address the operator
   *  signed in with even on a roll load — while the same word at 422 is about a line
   *  in the roster. Anything else falls back to the shared sentence, which is the
   *  safe direction: a status this does not recognise gets the words that were shown
   *  before. */
  const failed = useCallback(
    async (err: unknown, act: RefusalAct | null = null): Promise<void> => {
      const code = err instanceof DecidesApiError ? err.code : 'unreachable';
      const fromTheAct =
        err instanceof DecidesApiError && (err.status === 409 || err.status === 422);
      // The refusal body travels with the code. Empty for anything that was not a
      // deliberate refusal (a network failure has no body), so a screen reading a
      // figure out of it gets nothing rather than a stale one from the act before.
      const details = err instanceof DecidesApiError ? err.details : NO_REFUSAL_DETAILS;
      setActError({ code, act: fromTheAct ? act : null, details });
      if (code === 'wrong_state' || code === 'conflict') {
        try {
          await refreshDrafts();
        } catch {
          // Leave the stale list under the message; the operator can retry.
        }
      }
    },
    [refreshDrafts],
  );

  const doMarkReady = useCallback(
    async (ballotId: string): Promise<void> => {
      setBusy(true);
      setPending(ballotId);
      setActError(null);
      setNotice(null);
      try {
        await api.markReady(ballotId);
        await refreshDrafts();
        setNotice('The ballot is marked ready. Freeze the roll to open it.');
      } catch (err) {
        await failed(err);
      } finally {
        setBusy(false);
        setPending(null);
      }
    },
    [refreshDrafts, failed],
  );

  const doAbandon = useCallback(
    async (ballotId: string, reason: string): Promise<void> => {
      setBusy(true);
      setPending(ballotId);
      setActError(null);
      setNotice(null);
      try {
        await api.abandonBallot(ballotId, reason);
        await refreshDrafts();
        setNotice('The ballot is abandoned. It will not be used.');
      } catch (err) {
        await failed(err);
      } finally {
        setBusy(false);
        setPending(null);
      }
    },
    [refreshDrafts, failed],
  );

  const doFreeze = useCallback(async (): Promise<void> => {
    setBusy(true);
    setActError(null);
    setNotice(null);
    try {
      const result = await api.freezeRoll(electionId);
      // The roll changed to frozen, so re-read the whole hub (the register is now
      // available). Then land on the result heading.
      await reloadData();
      setLoaded(null);
      setView({ kind: 'roll-frozen', rollDigest: result.rollDigest });
      setNotice('The roll is frozen.');
    } catch (err) {
      await failed(err);
    } finally {
      setBusy(false);
    }
  }, [electionId, reloadData, failed]);

  const doOpen = useCallback(
    async (ballot: DraftBallot, quorum: QuorumDeclaration): Promise<void> => {
      setBusy(true);
      setActError(null);
      setNotice(null);
      try {
        await api.openBallot(ballot.id, quorum);
        await refreshDrafts();
        setView({ kind: 'ballot-opened', ballot });
        setNotice('The ballot is open. Members can now vote.');
      } catch (err) {
        // Stay on the open step so the operator can correct the figure; the error
        // is handed to it below.
        await failed(err);
      } finally {
        setBusy(false);
      }
    },
    [refreshDrafts, failed],
  );

  /**
   * GIVE A RUNNING POLL MORE TIME — the only act this surface has on an open ballot.
   *
   * **THE REPORT IS THE SERVER'S ANSWER, NOT THE REQUEST.** The title and the closing
   * time announced are the ones the worker sent back: extending the wrong ballot is
   * silent and cannot be undone, and the route has two idempotent 200s where the window
   * that stands is somebody ELSE's — a retry after a lost reply, or a second operator's
   * extension that landed first. Announcing what this screen believed it pressed would
   * report a change that did not happen, in the one place nobody would check.
   *
   * On a refusal the view STAYS on the extend step (the disposition `doOpen` set), so
   * the operator corrects the time in front of the sentence explaining it, with the
   * route's own figures beside it — rather than being returned to the hub to find their
   * way back to a poll that is still shut.
   */
  const doExtend = useCallback(
    async (ballot: DraftBallot, closesAtNz: string): Promise<void> => {
      // `busy` alone, not `pending`: that pair only identifies which ROW is acting, and
      // during an extend the operator is on the extend step, not on the hub's list. The
      // same disposition `doOpen` takes for the same reason.
      setBusy(true);
      setActError(null);
      setNotice(null);
      try {
        const answer = await api.extendBallot(ballot.id, closesAtNz);
        // The window moved, so the hub's row is stale the moment this returns — re-read
        // before the panel that states the close is shown again.
        await refreshDrafts();
        focusHubHeadingOnReturn.current = true;
        setView({ kind: 'hub' });
        setNotice(
          `“${answer.title}” now closes at ${wallClockInWords(answer.closesAtNz)}, New Zealand time.`,
        );
      } catch (err) {
        // Stay on the extend step so the operator can correct the time; the refusal and
        // its figures are handed to it below.
        await failed(err);
      } finally {
        setBusy(false);
      }
    },
    [refreshDrafts, failed],
  );

  /**
   * THE ONE PLACE A ROSTER BECOMES A ROLL. Both paths reach `POST …/roll` through
   * this line — the Zoho pull below and the roster the operator pasted — so the
   * count that is shown and the sentence that is announced cannot drift apart
   * between them, and a later change to either lands on both.
   *
   * It handles neither `busy` nor the refusal: it THROWS, and each caller owns its
   * own try/finally. A nested one would clear `busy` while the outer act was still
   * in flight, which unlocks every button on the screen mid-request.
   */
  const loadRoster = useCallback(
    async (electors: RosterElector[]): Promise<void> => {
      const result = await api.loadRoll(electionId, electors);
      setLoaded(result.loaded);
      setNotice(`Loaded ${String(result.loaded)} onto the roll.`);
    },
    [electionId],
  );

  const doPull = useCallback(async (): Promise<void> => {
    setBusy(true);
    setActError(null);
    setPullNote(null);
    setNotice(null);
    try {
      // The 404 "not built yet" note belongs to the PULL alone. Scope it to that
      // call, so once P3b lands a 404 from loadRoll is a real failure and flows
      // through `failed`, not this note.
      let roster: RosterPull;
      try {
        roster = await api.rosterPull(electionId);
      } catch (err) {
        if (err instanceof DecidesApiError && err.status === 404) {
          // P3b — the Zoho pull is not built. Not a failure: a plain note, kept out
          // of `actError` so it does not read as a broken request. It names the
          // paste path, so the operator meets the way that works in the same breath
          // as the one that does not.
          setPullNote(
            'Pulling the roll from Zoho is not available yet — it is the next phase of setup. Paste the roster below instead: export the electors from Zoho Contacts and paste them straight in.',
          );
          return;
        }
        throw err;
      }
      await loadRoster(roster.electors);
    } catch (err) {
      await failed(err, 'roll-load');
    } finally {
      setBusy(false);
    }
  }, [electionId, loadRoster, failed]);

  /** Load the roster the operator pasted into the roll panel. The panel has already
   *  parsed it and withheld its own button while any line was unreadable
   *  (`setup/roster.ts`); this is the request, and the server validates the roster
   *  again regardless — the client mirror is an address for a refusal, never a
   *  substitute for one (ARCH-2). */
  const doLoadRoster = useCallback(
    async (electors: RosterElector[]): Promise<void> => {
      setBusy(true);
      setActError(null);
      setPullNote(null);
      setNotice(null);
      try {
        await loadRoster(electors);
      } catch (err) {
        await failed(err, 'roll-load');
      } finally {
        setBusy(false);
      }
    },
    [loadRoster, failed],
  );

  const doCreate = useCallback(
    async (spec: CreateBallotSpec): Promise<void> => {
      setBusy(true);
      setActError(null);
      setNotice(null);
      try {
        await api.createBallot(electionId, spec);
        // A draft is not a point of no return; on success return to the hub, where the
        // new draft is now in the refreshed list, and announce it in the status channel.
        // Ask the hub to take focus on its heading (the Create button is unmounting).
        await refreshDrafts();
        focusHubHeadingOnReturn.current = true;
        setView({ kind: 'hub' });
        setNotice(
          'The ballot draft is created. It is on the list below, ready to build up and mark ready.',
        );
      } catch (err) {
        // Stay on the builder so the operator can correct the spec; the refusal is
        // handed to it below.
        await failed(err);
      } finally {
        setBusy(false);
      }
    },
    [electionId, refreshDrafts, failed],
  );

  // ── The load and refusal screens ─────────────────────────────────────────
  if (screen.status === 'loading') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <p role="status">Reading the setup…</p>
      </Frame>
    );
  }

  if (screen.status === 'refused') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">The setup is not open to you</h1>
        <p className="alert">{refusalSentence(screen.code)}</p>
        <button type="button" className="btn btn-ghost" onClick={() => void load()}>
          Try again
        </button>
        <ProblemsLine />
      </Frame>
    );
  }

  const { data } = screen;
  const mode = asBallotMode(data.sitting.mode);
  const rollFrozen = data.roll.phase === 'frozen';
  const reference = data.roll.phase === 'frozen' ? data.roll.register : [];

  // ── The ballot builder ────────────────────────────────────────────────────
  // `hideBack`: the builder owns its own dirty-guarded "Back to setup", so the
  // frame's unguarded back is suppressed here — otherwise a mis-tap on it discards
  // the whole in-progress draft with no confirm (Miranda's data-loss trap).
  if (view.kind === 'building') {
    return (
      <Frame identity={identity} onBack={onBack} mode={mode} hideBack>
        <BallotBuilder
          busy={busy}
          error={actError?.code ?? null}
          onCreate={(spec) => void doCreate(spec)}
          onCancel={() => {
            setActError(null);
            focusHubHeadingOnReturn.current = true;
            setView({ kind: 'hub' });
          }}
        />
        <ProblemsLine />
      </Frame>
    );
  }

  // ── The open step ────────────────────────────────────────────────────────
  // `hideBack`, for the builder's reason: the step owns its own guarded "Back to
  // setup", so the frame's unguarded back is suppressed — otherwise the two stack,
  // and a mis-tap on the frame's discards the typed quorum figures with no confirm
  // (cca-agfn).
  if (view.kind === 'opening') {
    return (
      <Frame identity={identity} onBack={onBack} mode={mode} hideBack>
        <OpenStep
          ballot={view.ballot}
          reference={reference}
          busy={busy}
          error={actError?.code ?? null}
          onOpen={(quorum) => void doOpen(view.ballot, quorum)}
          onCancel={() => {
            setActError(null);
            focusHubHeadingOnReturn.current = true;
            setView({ kind: 'hub' });
          }}
        />
        <ProblemsLine />
      </Frame>
    );
  }

  // ── The extend step ──────────────────────────────────────────────────────
  // The refusal's BODY goes down with its code: the route sends the close that stands,
  // the latest instant it would accept and the cap in hours precisely so the operator
  // is never told "no" without being told what to type instead.
  if (view.kind === 'extending') {
    return (
      <Frame identity={identity} onBack={onBack} mode={mode}>
        <ExtendStep
          ballot={view.ballot}
          busy={busy}
          error={actError?.code ?? null}
          errorDetails={actError?.details ?? NO_REFUSAL_DETAILS}
          onExtend={(closesAtNz) => void doExtend(view.ballot, closesAtNz)}
          onCancel={() => {
            setActError(null);
            setView({ kind: 'hub' });
          }}
        />
        <ProblemsLine />
      </Frame>
    );
  }

  // ── The two points-of-no-return results ──────────────────────────────────
  if (view.kind === 'roll-frozen') {
    return (
      <Frame identity={identity} onBack={onBack} mode={mode}>
        <FocusHeading>The roll is frozen.</FocusHeading>
        <p className="setup-lede">
          The roll is fixed as the record of who may vote at this sitting. You can now keep the
          attendance register and, once a ballot is ready, open it.
        </p>
        <dl className="setup-digest">
          <dt>Frozen roll digest</dt>
          <dd className="result-mono">{view.rollDigest}</dd>
        </dl>
        <a className="setup-register-link" href={`#register/${encodeURIComponent(electionId)}`}>
          Open the attendance register
        </a>
        <div className="setup-result-actions">
          <button
            type="button"
            className="btn btn-primary setup-primary"
            onClick={() => setView({ kind: 'hub' })}
          >
            Back to setup
          </button>
        </div>
        <ProblemsLine />
      </Frame>
    );
  }

  if (view.kind === 'ballot-opened') {
    return (
      <Frame identity={identity} onBack={onBack} mode={mode}>
        <FocusHeading>“{view.ballot.title}” is open.</FocusHeading>
        <p className="setup-lede">
          Members on the roll can now mark their papers. When Synod has finished, a Scrutineer
          closes the ballot, seals the papers and runs the count.
        </p>
        <div className="setup-result-actions">
          <button
            type="button"
            className="btn btn-primary setup-primary"
            onClick={() => setView({ kind: 'hub' })}
          >
            Back to setup
          </button>
        </div>
        <ProblemsLine />
      </Frame>
    );
  }

  // ── The hub ──────────────────────────────────────────────────────────────
  return (
    <Frame identity={identity} onBack={onBack} mode={mode}>
      <h1 className="ballot-title" tabIndex={-1} ref={hubHeading}>
        {data.sitting.name}
      </h1>
      <p className="setup-lede">
        Get this sitting ready to vote: build the roll and freeze it, take each ballot from draft to
        ready, and open a ballot when Synod is ready to decide it. Once a poll is open you can give
        it more time if Synod needs longer.
      </p>

      {/* THE ONE ANNOUNCEMENT REGION. Present always so a change is spoken; it
          carries a stage word and never a figure. */}
      <p className="setup-status" role="status">
        {notice ?? ''}
      </p>
      {actError !== null ? (
        <p className="alert" role="alert">
          {refusalSentence(actError.code, actError.act)}
        </p>
      ) : null}

      <div className="setup-panels">
        {/* FIRST IN THE STACK, and absent entirely when nothing is open (the panel
            returns null). A poll that is running now is the most time-critical thing on
            this screen: the roll is frozen and the ballots are built, and the only clock
            still going is that one. It reads the same `drafts` list as the panel below
            and filters it itself, so the hub holds one list and no second belief about
            which ballots are open. */}
        <OpenPollsPanel
          ballots={data.drafts}
          rollFrozen={rollFrozen}
          busy={busy}
          onExtend={(ballot) => {
            setActError(null);
            setNotice(null);
            setView({ kind: 'extending', ballot });
          }}
        />
        <RollPanel
          electionId={electionId}
          frozen={rollFrozen}
          rollDigest={data.roll.phase === 'frozen' ? data.roll.rollDigest : null}
          loaded={loaded}
          busy={busy}
          pullNote={pullNote}
          onPull={() => void doPull()}
          onLoadRoster={(electors) => void doLoadRoster(electors)}
          onFreeze={() => void doFreeze()}
        />
        <BallotsPanel
          ballots={data.drafts}
          rollFrozen={rollFrozen}
          busy={busy}
          pending={pending}
          onMarkReady={(id) => void doMarkReady(id)}
          onAbandon={(id, reason) => void doAbandon(id, reason)}
          onOpen={(ballot) => {
            setActError(null);
            setNotice(null);
            setView({ kind: 'opening', ballot });
          }}
          onBuild={() => {
            setActError(null);
            setNotice(null);
            setView({ kind: 'building' });
          }}
        />
      </div>

      <ProblemsLine />
    </Frame>
  );
}

/** The page frame every state shares — the estate header, the mode band where the
 *  mode is known, and this app's page column. The frame's back button returns to
 *  the landing; sub-views add their own "Back to setup". `hideBack` suppresses the
 *  frame's own back for the builder and the open step, each of which owns a
 *  dirty-guarded back of its own — two pixel-identical back buttons stacked would let
 *  a mis-tap discard unsaved work (a draft, or the typed quorum figures) with no
 *  confirm, bypassing the step's guard. */
function Frame({
  children,
  identity,
  onBack,
  mode,
  hideBack = false,
}: {
  children: ReactNode;
  identity: Identity;
  onBack: () => void;
  mode?: ReturnType<typeof asBallotMode>;
  hideBack?: boolean;
}): JSX.Element {
  const band = mode ?? null;
  return (
    <div className="ballot-app min-dvh">
      <PortalHeader portalName={portalNameFor(identity)} />
      {band === null ? null : <ModeBand mode={band} />}
      <main className="ballot-main">
        <div className="page-narrow">
          {hideBack ? null : (
            <button type="button" className="btn btn-ghost setup-back" onClick={onBack}>
              Back to your sittings
            </button>
          )}
          {children}
        </div>
      </main>
      {band === null ? null : <ModeBand mode={band} repeat />}
    </div>
  );
}

/** A result heading that takes focus when it mounts — the CastPage `Recorded`
 *  pattern (a `tabIndex={-1}` heading and {@link useFocusOnMount}), so the
 *  point-of-no-return result is announced by the heading the keyboard lands on
 *  rather than by a freshly-mounted live region several screen readers miss. */
function FocusHeading({ children }: { children: ReactNode }): JSX.Element {
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);
  return (
    <h1 className="ballot-title" tabIndex={-1} ref={heading}>
      {children}
    </h1>
  );
}

/** UX-7 — the escape hatch, on every screen: a way out that is not "try again".
 *  The registrar's address comes from one place (copy.ts). */
function ProblemsLine(): JSX.Element {
  return (
    <p className="setup-problems">
      Having any problems? Email <a href={`mailto:${REGISTRAR_EMAIL}`}>{REGISTRAR_EMAIL}</a>.
    </p>
  );
}
